import { useLayoutEffect, useRef } from "react"
import { useResizeObserver } from "@/utils/useResizeObserver"
import { usePointerDrag } from "@/utils/usePointerDrag"
import clsx from "clsx"

interface ScrollBarProps {
	parentRef?: React.RefObject<HTMLDivElement>
	style?: React.CSSProperties
	className?: string
	horizontal?: boolean
	visible?: "always" | "hover"
}

export const ScrollBar = (props: ScrollBarProps) => {
	const minSize = 64
	const containerRef = useRef<HTMLDivElement>(null)
	const barRef = useRef<HTMLDivElement>(null)

	const { observeRef, dimensions } = useResizeObserver({ observeChildren: true })

	const getParent = () => {
		return props.parentRef?.current ?? containerRef.current?.parentElement ?? null
	}

	const setScrollbarPosition = () => {
		const parent = getParent()
		const scrollBar = barRef.current
		const container = containerRef.current

		if (!scrollBar || !parent || !container) {
			return
		}

		if (props.horizontal) {
			const width = (parent.clientWidth / parent.scrollWidth) * parent.clientWidth
			const offset = minSize - Math.min(minSize, width)
			const x = (parent.scrollLeft / parent.scrollWidth) * (parent.clientWidth - offset)

			scrollBar.style.opacity = parent.clientWidth === parent.scrollWidth ? "0" : ""
			scrollBar.style.transform = `translateX(${x}px)`
			scrollBar.style.minWidth = `${minSize}px`
			scrollBar.style.width = `${width}px`
			container.style.width = `${dimensions.clientWidth}px`
		} else {
			const height = (parent.clientHeight / parent.scrollHeight) * parent.clientHeight
			const offset = minSize - Math.min(minSize, height)
			const y = (parent.scrollTop / parent.scrollHeight) * (parent.clientHeight - offset)

			scrollBar.style.opacity = parent.clientHeight === parent.scrollHeight ? "0" : ""
			scrollBar.style.transform = `translateY(${y}px)`
			scrollBar.style.minHeight = `${minSize}px`
			scrollBar.style.height = `${height}px`
			container.style.height = `${dimensions.clientHeight}px`
		}
	}

	useLayoutEffect(() => {
		const parent = getParent()

		if (!parent) {
			return
		}

		parent.addEventListener("scroll", setScrollbarPosition)

		return () => parent.removeEventListener("scroll", setScrollbarPosition)
	}, [props.horizontal])

	useLayoutEffect(() => {
		setScrollbarPosition()
	}, [
		dimensions.scrollWidth,
		dimensions.scrollHeight,
		dimensions.clientWidth,
		dimensions.clientHeight,
		props.horizontal,
	])

	useLayoutEffect(() => {
		observeRef(getParent())
	})

	const scrollTowardsPointer = (e: React.PointerEvent<HTMLDivElement>) => {
		const parent = getParent()
		const container = containerRef.current
		const bar = barRef.current

		if (!parent || !container || !bar) {
			return
		}

		if (e.target instanceof Node && bar.contains(e.target)) {
			return
		}

		const containerRect = container.getBoundingClientRect()
		const barRect = bar.getBoundingClientRect()
		const clamp = (value: number, min: number, max: number) =>
			Math.min(Math.max(value, min), max)

		if (props.horizontal) {
			const trackLength = containerRect.width
			const handleLength = barRect.width
			const maxScroll = parent.scrollWidth - parent.clientWidth
			const range = Math.max(trackLength - handleLength, 1)
			const offset = e.clientX - containerRect.left - handleLength / 2
			const ratio = clamp(offset / range, 0, 1)

			if (maxScroll <= 0) {
				return
			}

			parent.scrollLeft = clamp(ratio * maxScroll, 0, maxScroll)
		} else {
			const trackLength = containerRect.height
			const handleLength = barRect.height
			const maxScroll = parent.scrollHeight - parent.clientHeight
			const range = Math.max(trackLength - handleLength, 1)
			const offset = e.clientY - containerRect.top - handleLength / 2
			const ratio = clamp(offset / range, 0, 1)

			if (maxScroll <= 0) {
				return
			}

			parent.scrollTop = clamp(ratio * maxScroll, 0, maxScroll)
		}

		setScrollbarPosition()
	}

	const bindDrag = usePointerDrag<HTMLDivElement>({
		onDrag: (pointer) => {
			const parent = getParent()

			if (!parent) {
				return
			}

			const data = pointer.getData(() => {
				return {
					startScrollLeft: parent.scrollLeft,
					startScrollTop: parent.scrollTop,
				}
			})

			if (props.horizontal) {
				parent.scrollLeft =
					data.startScrollLeft +
					pointer.deltaX / (parent.clientWidth / parent.scrollWidth)
			} else {
				parent.scrollTop =
					data.startScrollTop +
					pointer.deltaY / (parent.clientHeight / parent.scrollHeight)
			}
		},
		onPointerDown: (e) => {
			if (e.button !== 0) {
				return
			}

			scrollTowardsPointer(e)
		},
	})

	return (
		<div
			ref={containerRef}
			className={clsx(
				"group/scrollbar absolute z-10 flex bg-transparent",
				props.visible === "hover" && "scrollbar-visible-hover",
				props.horizontal
					? "inset-x-0 bottom-0 h-4 items-center"
					: "inset-y-0 right-0 w-4 justify-end",
				props.className,
			)}
			style={props.style}
			{...bindDrag}
		>
			<div
				ref={barRef}
				className={clsx(
					"fixed rounded-full border-2 border-transparent bg-neutral-300 bg-clip-content opacity-70 transition group-hover/scrollbar:opacity-100",
					props.horizontal
						? "h-2.5 group-hover/scrollbar:h-3"
						: "w-2.5 group-hover/scrollbar:w-3",
				)}
				style={{
					transitionProperty: "opacity, height, width",
				}}
			></div>
		</div>
	)
}
